<template>
  <div class="input-cart" :class="atLeastOneItemIsSelected ? 'input-cart-has-items' : 'input-cart-no-items'">
    <lightbox :open="cartOpen" background="#000000" :background-opacity=".6" @close="cartOpen = false">
      <div class="input-cart-form">
        <!--<button class="btn close">
          <no-drag-image class="logo" src="@/assets/btn-close.png"
                         @click="onClickClose"
          />
        </button>-->

        <h1>{{ cartTitle }}</h1>
        <h2>{{ $t('misc.pickOneOrMore') }}</h2>

        <div class="category-container-wrapper td-200">
          <div class="category-container">
            <div v-for="(category, categoryIndex) in categories" :key="category.id" class="row-fluid">
              <div class="category" :class="[categoryIndex > 0 ? 'category-mt' : '', activeCategoryItemIds[category.id].length > 0 ? 'with-badge' : 'without-badge', category.hint ? 'with-hint' : 'without-hint']">
                <button class="btn btn-category text-center" @click="clickCategory(category)">
                  <span class="category-name">{{ getName(category) }}</span>
                  <hint-button v-if="category.hint" @click.prevent.stop="onClickHint(getName(category), category.hint)" />
                  <span
                    class="badge td-400 rounded-pill"
                    :class="activeCategoryItemIds[category.id].length > 0 ? 'badge-visible' : 'badge-hidden'"
                  >
                    {{ activeCategoryItemIds[category.id].length }}
                  </span>
                </button>

                <accordion :open="openCategoryIds.includes(category.id)">
                  <div class="item-container">
                    <div
                      v-for="(item, itemIndex) in category.items" :key="item.id"
                      :class="[itemIndex > 0 ? 'category-mt' : '']"
                    >
                      <input-button-toggle
                        :model-value="innerValue.includes(item.id)"
                        class="text-center btn-item"
                        :class="item.hint ? 'with-hint' : 'without-hint'"
                        @update:model-value="onButtonToggled(category, item, $event)"
                      >
                        <span class="item-name">{{ getName(item) }}</span>
                        <hint-button v-if="item.hint" @click.prevent.stop="onClickHint(getName(item), item.hint)" />
                      </input-button-toggle>
                    </div>
                  </div>
                </accordion>
              </div>
            </div>
          </div>
        </div>

        <div class="confirm-button-container td-200">
          <div class="row">
            <div class="col">
              <app-button @click="cartOpen = false">
                {{ $t('misc.goBack') }}
              </app-button>
            </div>
            <div class="col">
              <app-button :disabled="!atLeastOneItemIsSelected" @click="cartOpen = false">
                {{ addInsideLabel }}
              </app-button>
            </div>
          </div>
        </div>
      </div>
    </lightbox>
    <lightbox :open="showNoProductWarningPopup" background="#000000" :background-opacity=".6" @close="showNoProductWarningPopup = false">
      <div class="input-warning">
        <div class="row">
          <p>{{ noWarningText }}</p>
        </div>
        <div class="row">
          <div class="col">
            <input-button text-align="center" @click="showNoProductWarningPopup = false">
              {{ noProductWarningGoBackLabel }}
            </input-button>
          </div>
          <div class="col">
            <input-button state="active" text-align="center" @click="onClickContinueNoProducts">
              {{ noProductWarningContinueLabel }}
            </input-button>
          </div>
        </div>
      </div>
    </lightbox>

    <div class="content-container container">
      <div v-if="innerValue && innerValue.length" class="row mb-4 cart-items-container">
        <div v-for="(itemId, n) in innerValue" :class="n > 0 ? 'mt-2' : ''">
          <input-button state="active" text-align="center" class="btn-remove-from-cart-main">
            {{ getName(itemsAssoc[itemId], true) }}
            <button class="btn btn-remove-from-cart" @click="removeFromCart(itemsAssoc[itemId])">
              <no-drag-image src="@/assets/btn-close-white.png" />
            </button>
          </input-button>
        </div>
      </div>
      <div class="row narrow-button-container mb-2">
        <input-button state="active" text-align="center" @click="onClickAdd">
          {{ addLabel }}
          <span
            class="badge rounded-pill badge-white"
            :class="innerValue.length > 0 ? 'badge-visible' : 'badge-hidden'"
          >
            {{ innerValue.length }}
          </span>
        </input-button>
      </div>
      <div v-if="showNoButton" class="row narrow-button-container">
        <input-button state="" text-align="center" :disabled="!canClickNo" @click="onClickNo">
          {{ noLabel }}
        </input-button>
      </div>
    </div>

    <lightbox :open="currentHint !== null" background="#ffffff" :background-opacity=".9" @close="currentHint = null">
      <div class="hint-button-popup">
        <div class="row">
          <h1>{{ currentHint.title }}</h1>
        </div>
        <div class="row">
          <markdown-text>{{ currentHint.hint }}</markdown-text>
        </div>
        <div class="row">
          <app-button @click="currentHint = null">
            {{ $t('cart.hintClose') }}
          </app-button>
        </div>
      </div>
    </lightbox>
  </div>
</template>

<script>

import { mapState } from 'pinia';
import { useSurveyStore } from '@/store';
import { compareArrays, removeArrayItemByValue, toggleArrayItemByValue } from '@/utils/arrayUtil';
import InputComponent from '../InputComponent';
import NoDragImage from '../../common/NoDragImage';
import InputButtonToggle from '../common/InputButton/InputButtonToggle';
import Accordion from '../../common/Accordion';
import Lightbox from '../../common/Lightbox';
import InputButton from '../common/InputButton/InputButton';
import AppButton from '@/components/common/AppButton';
import HintButton from './HintButton';
import MarkdownText from '../../common/MarkdownText';

export default {
  name: 'InputCart',
  components: {
    MarkdownText,
    AppButton,
    InputButton,
    InputButtonToggle,
    NoDragImage,
    Accordion,
    Lightbox,
    HintButton,
  },
  extends: InputComponent,
  props: {
    modelValue: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
    categories: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
    cartTitle: {
      type: String,
      required: true,
    },
    addLabel: {
      type: String,
      default: 'Add items',
      required: false,
    },
    addInsideLabel: {
      type: String,
      required: false,
      default() {
        return this.addLabel;
      },
    },
    noLabel: {
      type: String,
      default: 'No items',
      required: false,
    },
    showNoButton: {
      type: Boolean,
      default: true,
      required: false,
    },
    noWarningText: {
      type: String,
      default: 'Are you sure?',
      required: false,
    },
  },
  emits: ['update:modelValue', 'continue'],
  data() {
    return {
      innerValue: null,

      cartOpen: false,

      openCategoryIds: [],

      activeCategoryItemIds: {},

      itemsAssoc: {},

      showNoProductWarningPopup: false,

      currentHint: null,
    };
  },
  computed: {
    ...mapState(useSurveyStore, ['language', 'disableTransitions']),

    atLeastOneItemIsSelected() {
      if (!this.innerValue) {
        return false;
      }

      return this.innerValue.length > 0;
    },

    transitionDuration() {
      return this.disableTransitions ? 1 : 800;
    },

    noProductWarningGoBackLabel() {
      return this.$t('cart.noProductWarningGoBack');
    },
    noProductWarningContinueLabel() {
      return this.$t('cart.noProductWarningContinue');
      // return 'Continue';
    },
    canClickNo() {
      return true;

      // return !this.innerValue || this.innerValue.length === 0;
    },
  },
  watch: {
    cartOpen() {
      if (this.cartOpen) {
        this.openCategoryIds = [];
      }
    },
    categories: {
      handler() {
        this.setActiveCategoryItemIdsFromCategories();
      },
      deep: true,
    },
    modelValue: {
      handler() {
        const newInnerValue = compareArrays(this.modelValue, this.innerValue, true);

        if (newInnerValue === true) {
          // It's already the same values
          return;
        }

        this.innerValue = newInnerValue;
      },
      deep: true,
    },
    innerValue: {
      handler() {
        const newValue = compareArrays(this.innerValue, this.modelValue, true);

        this.setActiveCategoryItemIdsFromCategories();

        if (newValue === true) {
          // It's already the same values
          return;
        }

        this.$emit('update:modelValue', newValue);
      },
      deep: true,
    },
  },
  beforeMount() {
    if (!this.innerValue) {
      this.innerValue = this.modelValue || [];
    }
  },
  mounted() {
    this.setActiveCategoryItemIdsFromCategories();
  },
  methods: {
    onClickHint(title, hint) {
      this.currentHint = {
        title,
        hint,
      };
    },
    removeFromCart(item) {
      removeArrayItemByValue(this.innerValue, item.id);
    },
    onButtonToggled(category, item, toggled) {
      // console.log('innerValue', JSON.stringify(this.innerValue));
      toggleArrayItemByValue(this.innerValue, item.id, toggled);

      // console.log('activeCategoryItemIds', JSON.stringify(this.activeCategoryItemIds));

      // console.log('category', category.id, 'item', item.id, 'toggled', toggled);

      toggleArrayItemByValue(this.activeCategoryItemIds[category.id], item.id, toggled);
    },
    setActiveCategoryItemIdsFromCategories() {
      // console.log('JSON.stringify', JSON.stringify(this.innerValue));
      for (const category of this.categories) {
        this.activeCategoryItemIds[category.id] = [];

        for (const item of category.items) {
          item.categoryId = category.id;

          this.itemsAssoc[item.id] = item;

          const included = this.innerValue.includes(item.id);

          if (included) {
            this.activeCategoryItemIds[category.id].push(item.id);
          }
        }
      }
    },
    clickCategory(category) {
      toggleArrayItemByValue(this.openCategoryIds, category.id);
    },
    getName(item, withCategory) {
      if (withCategory) {
        let categoryName;

        for (const category of this.categories) {
          if (category.id === item.categoryId) {
            categoryName = category.name;
          }
        }

        if (categoryName) {
          return `${categoryName} - ${item.name}`;
        }
      }

      return item.name;
    },
    onClickClose() {
      this.cartOpen = false;
    },
    onClickAdd() {
      this.cartOpen = true;
    },
    onClickNo() {
      if (!this.canClickNo) {
        return;
      }

      this.showNoProductWarningPopup = true;
    },
    onClickContinueNoProducts() {
      this.$emit('continue');
    },
  },
};

</script>

<style lang="scss" scoped>
.input-cart-form {
  background: #F8FAFB;
  border-radius: 10px;
  height: 100%;

  filter: drop-shadow(0px 10px 20px rgba(0, 0, 0, 0.25));
  padding: 15px;
  overflow: hidden;
}

.category-container-wrapper {
  padding: 80px 20px 20px 20px;
  transition-property: padding-bottom;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;

  padding-bottom: 70px; // Because it should always be open
}

.confirm-button-container {
  position: absolute;
  //bottom: -80px;
  bottom: 0; // Because it should always be open
  left: 0;
  width: 100%;
  text-align: center;
  padding: 10px 20px;
  transition-property: bottom;

  .app-button {
    width: 100% !important;
  }
}

.input-cart-has-items {
  .category-container-wrapper {
    //padding-bottom: 70px;
  }

  .confirm-button-container {
    //bottom: 0;
  }
}

.category-container {
  background: #DFE5E9;
  border-radius: 10px;
  padding: 15px;
  overflow-y: scroll;
  height: 100%;
  width: 100%;
}

.item-container {
  padding-top: 10px;
}

.btn-category {
  text-align: center;
  width: 100%;

  outline: 0 !important;
  border: 0 !important;
  box-shadow: none !important;
  position: relative;

  &:focus {
    outline: 0 !important;
    border: 0 !important;
    box-shadow: none !important;
  }

  .hint-button {
    top: 3px !important;
  }
}

.btn-remove-from-cart-main {
  position: relative;
}

.btn-remove-from-cart {
  position: absolute !important;
  top: 0;
  right: 5px;
  padding: 5px;
  margin: 0;
  opacity: .8;

  &:focus, &:active {
    outline: 0 !important;
    border: 0 !important;
    box-shadow: none !important;
  }

  &:hover {
    opacity: 1;
  }

  img {
    width: 15px;
    height: 15px;
  }
}

.input-button, .btn {
  position: relative;
}

.input-button .badge, .btn .badge {
  position: absolute;
  top: 10px !important;
  left: 10px !important;
  bottom: auto !important;
  right: auto !important;
}

.badge {
  opacity: 0;
  background: #45A4CD;
  color: #EDF1F4;

  transition-timing-function: ease-out;
  transition-property: opacity;
  //transition-duration: 400ms;

  &.badge-white {
    background: #C7D3DA;
    color: #557F97;
  }

  &.badge-visible {
    opacity: 1;
  }
}

.category {
  background: #EDF1F4;
  border-radius: 10px;
  padding: 10px;
}

.category {
  &.with-badge {
    .btn-category {
      padding-left: 40px;
    }
  }

  &.with-hint {
    .btn-category {
      padding-right: 40px;
    }
  }
}

.btn-item {
  &.with-hint {
    padding-right: 40px;
  }
}

select {
  height: 800px;
}

h1, h2 {
  font-weight: 400;
  font-size: 13pt;
  text-align: center;
}

h1 {
  color: #063C5A;
}

h2 {
  color: #C7D3DA;
  font-style: italic;
}

.close {
  position: absolute;
  top: 12px;
  right: 15px;
  z-index: 1000000000000;
  padding: 0;

  img {
    width: 20px;
    height: 20px;
  }
}

.category-mt {
  margin-top: 10px;
}

.cart-items-container {
  background: #D9E3EA;
  border-radius: 10px;
  padding: 10px 0;
}

.narrow-button-container {
  max-width: 80%;
  margin: 0 auto;
}

.input-warning {
  background: #D9E3EA;
  border-radius: 10px;
  max-width: 100%;
  width: 400px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 30px;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 10px 36px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px;
  border: 2px solid #C7D3DA;

  p {
    color: #000000;
    text-align: center;
  }
}
</style>

<style lang="scss">
.hint-button-popup {
  text-align: center;

  position: relative;

  .app-button {
    margin: 15px auto 0 auto;
  }

  h1 {
    font-weight: bold;
    font-size: 18pt;
  }

  p {
    font-size: 14pt;
    text-align: center;
    max-width: 800px;
    margin: 0 auto;
    line-height: 160%;
  }
}
</style>
